import {
  UnmanagedBusinessAutoRegisterConditionFieldType,
  UnmanagedBusinessAutoRegisterField,
  UnmanagedBusinessAutoRegisterSettingItem,
} from '@/coreDomainsModule/constants/unmanagedBusinessAutoRegister';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { useForms } from '@/corePagesModule/composables/useForm';
import { i18n } from '@/i18n';

const t = i18n.t;

/**
 * 自動登録設定フォーム検証コンポーザブルの入力
 */
type UseUnmanagedBusinessAutoRegisterFormValidationOptions = {
  getFormSetting: () => UnmanagedBusinessAutoRegisterSettingItem;
  getNumericInputValues: () => {
    leadTime: string;
    stdInventoryIndicesMin: string;
    stdInventoryIndicesMax: string;
  };
  requiredConditionFieldTypes: UnmanagedBusinessAutoRegisterConditionFieldType[];
  numericOptionalRegisterFields: Set<UnmanagedBusinessAutoRegisterField>;
  getFieldLabel: (targetField: UnmanagedBusinessAutoRegisterField) => string;
};

/**
 * 自動登録設定フォームの検証ロジックを提供する
 * @param options - 検証対象フォームの取得方法と判定設定
 */
export const useUnmanagedBusinessAutoRegisterFormValidation = (options: UseUnmanagedBusinessAutoRegisterFormValidationOptions) => {
  const { getFormSetting, getNumericInputValues, requiredConditionFieldTypes, numericOptionalRegisterFields, getFieldLabel } = options;

  const formValidationFieldItems: FieldItem[] = [
    {
      name: 'settingName',
      rules: [ValidateRule.Required],
    },
    {
      name: 'conditionValues',
      rules: [],
      customValidateFunctions: [
        () => {
          const formSetting = getFormSetting();
          if (formSetting.conditions.length !== requiredConditionFieldTypes.length) {
            return '必須KEYが不足しています';
          }
          return undefined;
        },
        () => {
          const formSetting = getFormSetting();
          const hasAllEmptyConditionValues = formSetting.conditions.every((conditionItem) => conditionItem.targetValue.trim() === '');
          if (hasAllEmptyConditionValues) {
            return '必須KEY6項目のうち1項目以上の値を選択してください';
          }
          return undefined;
        },
      ],
    },
    {
      name: 'leadTime',
      rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
    },
    {
      name: 'stdInventoryIndicesMin',
      rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
      customValidateFunctions: [
        () => {
          const numericInputValues = getNumericInputValues();
          const stdInventoryIndicesMin = Number(numericInputValues.stdInventoryIndicesMin);
          const stdInventoryIndicesMax = Number(numericInputValues.stdInventoryIndicesMax);
          if (!Number.isFinite(stdInventoryIndicesMin) || !Number.isFinite(stdInventoryIndicesMax)) {
            return undefined;
          }
          if (stdInventoryIndicesMin > stdInventoryIndicesMax) {
            return '標準在庫指数minはmax以下にしてください';
          }
          return undefined;
        },
      ],
    },
    {
      name: 'stdInventoryIndicesMax',
      rules: [ValidateRule.Required, ValidateRule.HalfWidthRealNumber, ValidateRule.NegativeNumber],
      customValidateFunctions: [
        () => {
          const numericInputValues = getNumericInputValues();
          const stdInventoryIndicesMin = Number(numericInputValues.stdInventoryIndicesMin);
          const stdInventoryIndicesMax = Number(numericInputValues.stdInventoryIndicesMax);
          if (!Number.isFinite(stdInventoryIndicesMin) || !Number.isFinite(stdInventoryIndicesMax)) {
            return undefined;
          }
          if (stdInventoryIndicesMin > stdInventoryIndicesMax) {
            return '標準在庫指数minはmax以下にしてください';
          }
          return undefined;
        },
      ],
    },
    {
      name: 'optionalItems',
      rules: [],
      customValidateFunctions: [
        () => {
          const formSetting = getFormSetting();
          const invalidOptionalItem = formSetting.registerContent.optionalItems.find((optionalItem) => {
            const normalizedTargetValue = optionalItem.targetValue.trim();
            if (normalizedTargetValue === '') {
              return false;
            }
            if (!numericOptionalRegisterFields.has(optionalItem.targetField)) {
              return false;
            }
            const numericTargetValue = Number(normalizedTargetValue);
            return Number.isFinite(numericTargetValue) === false || numericTargetValue < 0;
          });
          if (invalidOptionalItem == null) {
            return undefined;
          }
          return t('{fieldLabel}には0以上の数値を入力してください', {
            fieldLabel: getFieldLabel(invalidOptionalItem.targetField),
          }) as string;
        },
      ],
    },
  ];

  const { fieldState, allValidate, resetForm } = useForms(formValidationFieldItems);

  /**
   * 条件入力の検証用シリアライズ文字列を作成する
   */
  const serializeConditionValuesForValidation = (): string => {
    const formSetting = getFormSetting();
    return JSON.stringify(
      formSetting.conditions.map((conditionItem) => ({
        targetFieldType: conditionItem.targetFieldType,
        searchType: conditionItem.searchType,
        targetValue: conditionItem.targetValue,
      }))
    );
  };

  /**
   * 任意項目入力の検証用シリアライズ文字列を作成する
   */
  const serializeOptionalItemsForValidation = (): string => {
    const formSetting = getFormSetting();
    return JSON.stringify(
      formSetting.registerContent.optionalItems.map((optionalItem) => ({
        targetField: optionalItem.targetField,
        targetValue: optionalItem.targetValue,
      }))
    );
  };

  /**
   * useFormの検証状態へ現在のフォーム値を反映する
   * @param shouldValidate - 反映時にバリデーションを実行するか
   */
  const syncFormValidationState = (shouldValidate = false): void => {
    const formSetting = getFormSetting();
    const numericInputValues = getNumericInputValues();
    fieldState.settingName.setValue(formSetting.settingName, shouldValidate);
    fieldState.conditionValues.setValue(serializeConditionValuesForValidation(), shouldValidate);
    fieldState.leadTime.setValue(numericInputValues.leadTime, shouldValidate);
    fieldState.stdInventoryIndicesMin.setValue(numericInputValues.stdInventoryIndicesMin, shouldValidate);
    fieldState.stdInventoryIndicesMax.setValue(numericInputValues.stdInventoryIndicesMax, shouldValidate);
    fieldState.optionalItems.setValue(serializeOptionalItemsForValidation(), shouldValidate);
  };

  /**
   * 条件入力のエラー表示中のみ再検証する
   */
  const revalidateConditionValuesIfNeeded = (): void => {
    fieldState.conditionValues.setValue(serializeConditionValuesForValidation(), fieldState.conditionValues.isError.value);
  };

  /**
   * 任意項目入力のエラー表示中のみ再検証する
   */
  const revalidateOptionalItemsIfNeeded = (): void => {
    fieldState.optionalItems.setValue(serializeOptionalItemsForValidation(), fieldState.optionalItems.isError.value);
  };

  /**
   * useFormで検出した最初のエラーメッセージを取得する
   */
  const getFirstUseFormErrorMessage = (): string | undefined => {
    const validationFieldNames = [
      'settingName',
      'conditionValues',
      'leadTime',
      'stdInventoryIndicesMin',
      'stdInventoryIndicesMax',
      'optionalItems',
    ];
    const firstErrorField = validationFieldNames.find((fieldName) => fieldState[fieldName].isError.value);
    if (firstErrorField == null) {
      return undefined;
    }
    return t(fieldState[firstErrorField].errorMessage.value) as string;
  };

  /**
   * 設定名入力の検証状態を更新する
   * @param value - 入力値
   */
  const updateSettingNameValidation = (value: string): void => {
    fieldState.settingName.setValue(value);
  };

  /**
   * リードタイム入力の検証状態を更新する
   * @param value - 入力値
   */
  const updateLeadTimeValidation = (value: string): void => {
    fieldState.leadTime.setValue(value);
  };

  /**
   * 標準在庫指数min入力の検証状態を更新する
   * @param value - 入力値
   */
  const updateStdInventoryIndicesMinValidation = (value: string): void => {
    const numericInputValues = getNumericInputValues();
    fieldState.stdInventoryIndicesMin.setValue(value);
    fieldState.stdInventoryIndicesMax.setValue(numericInputValues.stdInventoryIndicesMax, fieldState.stdInventoryIndicesMax.isError.value);
  };

  /**
   * 標準在庫指数max入力の検証状態を更新する
   * @param value - 入力値
   */
  const updateStdInventoryIndicesMaxValidation = (value: string): void => {
    const numericInputValues = getNumericInputValues();
    fieldState.stdInventoryIndicesMax.setValue(value);
    fieldState.stdInventoryIndicesMin.setValue(numericInputValues.stdInventoryIndicesMin, fieldState.stdInventoryIndicesMin.isError.value);
  };

  /**
   * 入力内容をバリデーションする
   */
  const validateForm = (): string | undefined => {
    syncFormValidationState(true);
    const isValidByUseForm = allValidate();
    if (!isValidByUseForm) {
      return getFirstUseFormErrorMessage();
    }
    return undefined;
  };

  /**
   * フォーム検証状態を初期化する
   */
  const resetValidationState = (): void => {
    resetForm(formValidationFieldItems);
  };

  return {
    fieldState,
    syncFormValidationState,
    revalidateConditionValuesIfNeeded,
    revalidateOptionalItemsIfNeeded,
    updateSettingNameValidation,
    updateLeadTimeValidation,
    updateStdInventoryIndicesMinValidation,
    updateStdInventoryIndicesMaxValidation,
    validateForm,
    resetValidationState,
  };
};
